import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, KeyRound, Activity, X, ShieldCheck } from "lucide-react";
import { useDemoAccess } from "@/lib/demoAccess";
import { TokenEntryModal } from "@/components/DemoAccessGate";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const LS_DISMISSED = "forgeops.demo.banner.dismissed.v1";

/**
 * DemoBanner — Operational Demo Access Console
 *
 * Premium, restrained, enterprise-grade access strip pinned at the top of
 * every page in demo mode. Replaces the legacy bright-orange "live demo"
 * marketing strip with a dark mission-critical operational chrome that
 * matches the rest of the platform.
 *
 *   - Credentials hidden behind a vault-style reveal (no plain-text
 *     password sitting in the chrome)
 *   - Reset Demo Data is intercepted by the global DemoActionGuard
 *     (matches `demo-banner-reseed` testid). The native confirm() never
 *     fires in the demo — the explainer modal does.
 *   - Dismissable per-session.
 */
export default function DemoBanner() {
  const [demoState, setDemoState] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [credsRevealed, setCredsRevealed] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const { access, signOut } = useDemoAccess();

  useEffect(() => {
    try {
      if (sessionStorage.getItem(LS_DISMISSED) === "1") setDismissed(true);
    } catch {
      /* private mode */
    }
    let cancel = false;
    axios
      .get(`${API}/demo/public/state`, { timeout: 8000 })
      .then((r) => {
        if (!cancel) setDemoState(r?.data || null);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, []);

  if (!demoState?.demo_mode || dismissed) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(LS_DISMISSED, "1");
    } catch {
      /* */
    }
    setDismissed(true);
  };

  // Reseed handler — exists for completeness but the global
  // DemoActionGuard intercepts the click via `demo-banner-reseed` testid.
  const reseed = async () => {
    try {
      const adminToken =
        localStorage.getItem("masci.admin.token") ||
        localStorage.getItem("masci.pm.token") ||
        "";
      const headers = adminToken
        ? localStorage.getItem("masci.admin.token")
          ? { "X-Admin-Token": adminToken }
          : { "X-Pm-Token": adminToken }
        : {};
      await axios.post(`${API}/demo/reseed`, {}, { headers, timeout: 60000 });
      toast.success("Demo data reset.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const isAdminContext =
    typeof window !== "undefined" &&
    /^\/(admin|pm|shop)/.test(window.location.pathname);

  return (
    <div
      className="relative w-full"
      style={{
        background: "#0A0A0A",
        borderBottom: "1px solid #1A1A1A",
        boxShadow: "0 1px 0 rgba(255,106,0,0.18)",
      }}
      data-testid="forgedops-demo-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3 flex-wrap">
        {/* Status pulse */}
        <span
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] font-bold"
          style={{ color: "#FF6A00" }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{
              background: "#FF6A00",
              boxShadow: "0 0 6px rgba(255,106,0,0.8)",
              animation: "fo-pulse 2.4s ease-in-out infinite",
            }}
          />
          Operational Demo Access Console
        </span>

        <span
          className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.25em]"
          style={{ color: "#666" }}
        >
          ·
        </span>

        <span
          className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.25em]"
          style={{ color: "#8A8A8A" }}
        >
          Role Simulation · Live Workflow Routing · Persistent Submissions
        </span>

        {/* Sandbox access mode pill (PUBLIC PREVIEW / SANDBOX / QUALIFIED) */}
        <AccessModePill access={access} />

        {/* Credential reveal — vault style */}
        <div className="flex items-center gap-2 ml-auto">
          {!access?.valid ? (
            <button
              type="button"
              onClick={() => setShowTokenModal(true)}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] font-bold h-7 px-2.5 rounded transition-colors"
              style={{
                background: "#1A1A1A",
                color: "#FFFFFF",
                border: "1px solid #2A2A2A",
              }}
              data-testid="demo-banner-enter-token"
              title="Enter operational sandbox access key"
            >
              <KeyRound className="w-3 h-3" style={{ color: "#FF6A00" }} />
              Enter access key
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { signOut(); toast.success("Sandbox session ended."); }}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] font-bold h-7 px-2.5 rounded transition-colors"
              style={{
                background: "#1A1A1A",
                color: "#FFFFFF",
                border: "1px solid #2A2A2A",
              }}
              data-testid="demo-banner-end-sandbox"
              title="End sandbox session"
            >
              End sandbox
            </button>
          )}

          {credsRevealed ? (
            <span
              className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] px-2.5 py-1 rounded"
              style={{
                background: "#1A1A1A",
                color: "#FFFFFF",
                border: "1px solid #2A2A2A",
              }}
              data-testid="demo-banner-creds-revealed"
            >
              <KeyRound className="w-3 h-3" style={{ color: "#FF6A00" }} />
              <span style={{ color: "#8A8A8A" }}>ACCESS KEY:</span>
              <code style={{ color: "#FFFFFF" }}>Demo2026!</code>
              <Link
                to="/demo"
                className="ml-2 inline-flex items-center gap-1 underline-offset-2 hover:underline"
                style={{ color: "#FF6A00" }}
                data-testid="demo-banner-open-console"
              >
                Open console →
              </Link>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setCredsRevealed(true)}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] font-bold h-7 px-2.5 rounded transition-colors"
              style={{
                background: "#1A1A1A",
                color: "#FFFFFF",
                border: "1px solid #2A2A2A",
              }}
              data-testid="demo-banner-creds-reveal"
              title="Reveal demo access key"
            >
              <Lock className="w-3 h-3" style={{ color: "#FF6A00" }} />
              Reveal Access Key
            </button>
          )}

          {isAdminContext && (
            <button
              onClick={reseed}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] font-bold h-7 px-2.5 rounded transition-colors"
              style={{
                background: "#1A1A1A",
                color: "#FFFFFF",
                border: "1px solid #2A2A2A",
              }}
              data-testid="demo-banner-reseed"
              title="Reseed demo data"
            >
              <Activity className="w-3 h-3" style={{ color: "#FF6A00" }} />
              Reseed
            </button>
          )}

          <button
            onClick={dismiss}
            className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors"
            style={{
              background: "transparent",
              color: "#666",
              border: "1px solid #2A2A2A",
            }}
            data-testid="demo-banner-dismiss"
            title="Hide for this session"
            aria-label="Dismiss demo banner"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Subtle bottom hairline (orange) */}
      <div
        aria-hidden
        className="absolute left-0 right-0 bottom-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,106,0,0.4) 50%, transparent 100%)",
        }}
      />

      <style>{`
        @keyframes fo-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
      `}</style>
      {showTokenModal && (
        <TokenEntryModal
          onClose={() => setShowTokenModal(false)}
          onSuccess={() => { window.location.reload(); }}
        />
      )}
    </div>
  );
}

/* Compact pill that surfaces the current Operational Sandbox access mode.
 * Public Preview = dim slate · Sandbox = orange · Qualified = orange+halo. */
function AccessModePill({ access }) {
  const level = access?.access_level || "public_preview";
  let label, sub, color, border;
  if (level === "qualified") {
    label = "QUALIFIED";
    sub   = access?.company || "Sandbox";
    color = "#FF6A00";
    border = "rgba(255,106,0,0.45)";
  } else if (level === "sandbox") {
    label = "SANDBOX";
    sub   = access?.company || "Operational";
    color = "#FF6A00";
    border = "#2A2A2A";
  } else {
    label = "PUBLIC PREVIEW";
    sub   = "Landing · Auto-tour";
    color = "#8A8A8A";
    border = "#1A1A1A";
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded"
      style={{
        background: "#101010",
        color,
        border: `1px solid ${border}`,
        boxShadow: level === "qualified" ? "0 0 8px rgba(255,106,0,0.18) inset" : "none",
      }}
      data-testid={`demo-access-mode-pill-${level}`}
    >
      <ShieldCheck className="w-3 h-3" />
      <span style={{ color }}>{label}</span>
      <span style={{ color: "#666" }}>·</span>
      <span style={{ color: "#9A9A9A" }}>{sub}</span>
    </span>
  );
}
